/**
 * PlantasIotAlarmasModal.jsx  v1
 * Historial de alarmas de toda la planta (base plantas_iot)
 */

import { useState, useEffect } from 'react';
import {
    Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
    FormControl, IconButton, MenuItem, Paper, Select, Stack, Typography,
    alpha, useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    NotificationsActive as AlarmIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import RangoFechas, { mesActual } from '../alvear/Rangofechas';

const LIMITE = 1000;

// Color de la alarma según su tipo: HH / LL críticas, H / L avisos
function severidad(tipo) {
    const t = (tipo || '').toUpperCase();
    if (t.endsWith('_HH') || t.endsWith('_LL')) return 'error';
    if (t.endsWith('_H') || t.endsWith('_L')) return 'warning';
    return 'info';
}

export default function PlantasIotAlarmasModal({ open, plantaId, tanques, apiUrl, onClose }) {
    const theme = useTheme();
    const [rango, setRango] = useState(() => mesActual());
    const [filtro, setFiltro] = useState('todos');   // 'todos' | '0' (planta) | tanque_id
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(false);

    const headers = { 'x-api-key': process.env.REACT_APP_HMI_API_KEY || '' };

    useEffect(() => {
        if (open) {
            setRango(mesActual());
            setFiltro('todos');
        }
    }, [open]);

    useEffect(() => {
        if (!open || !plantaId) return;
        let activo = true;
        setLoading(true);
        const tq = filtro === 'todos' ? '' : `&tanqueId=${filtro}`;
        fetch(`${apiUrl}/api/plantas-iot/${plantaId}/eventos?from=${encodeURIComponent(rango.from)}&to=${encodeURIComponent(rango.to)}${tq}&limit=${LIMITE}`, { headers })
            .then((r) => r.json())
            .then((d) => activo && setEventos(d.eventos || []))
            .catch(() => activo && setEventos([]))
            .finally(() => activo && setLoading(false));
        return () => { activo = false; };
    }, [open, plantaId, rango, filtro, apiUrl]);

    // Cantidad por tipo, de mayor a menor
    const porTipo = Object.entries(
        eventos.reduce((acc, e) => { acc[e.tipo] = (acc[e.tipo] || 0) + 1; return acc; }, {})
    ).sort((a, b) => b[1] - a[1]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 2, maxHeight: '92vh' } }}>

            <DialogTitle sx={{ pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        Alarmas — historial
                    </Typography>
                    <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }} sx={{ mt: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                        <RangoFechas value={rango} onChange={setRango} />
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select value={filtro} onChange={(e) => setFiltro(e.target.value)} sx={{ fontSize: 12 }}>
                            <MenuItem value="todos" sx={{ fontSize: 12 }}>Todos los tanques</MenuItem>
                            {(tanques || []).map((t) => (
                                <MenuItem key={t.tanque_id} value={String(t.tanque_id)} sx={{ fontSize: 12 }}>{t.tanque}</MenuItem>
                            ))}
                            <MenuItem value="0" sx={{ fontSize: 12 }}>Planta (generales)</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {loading ? (
                    <Stack alignItems="center" py={4}>
                        <CircularProgress size={28} />
                        <Typography variant="caption" color="text.disabled" mt={1}>Cargando alarmas...</Typography>
                    </Stack>
                ) : eventos.length === 0 ? (
                    <Stack alignItems="center" py={4}>
                        <Typography variant="body2" color="text.disabled">Sin alarmas en el período seleccionado</Typography>
                    </Stack>
                ) : (
                    <>
                        {/* Resumen por tipo */}
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                            <Typography variant="caption" color="text.disabled"
                                sx={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1 }}>
                                Resumen del período · {eventos.length} alarma{eventos.length === 1 ? '' : 's'}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                {porTipo.map(([tipo, cant]) => {
                                    const sev = severidad(tipo);
                                    return (
                                        <Chip key={tipo} size="small" label={`${tipo}: ${cant}`}
                                            sx={{
                                                height: 22, fontSize: 10, fontWeight: 700,
                                                bgcolor: alpha(theme.palette[sev].main, 0.12), color: `${sev}.main`,
                                            }}
                                        />
                                    );
                                })}
                            </Stack>
                            {eventos.length >= LIMITE && (
                                <Typography variant="caption" color="warning.main" display="block" mt={1} fontSize={10}>
                                    Se muestran las {LIMITE} más recientes. Achicá el rango para ver el resto.
                                </Typography>
                            )}
                        </Paper>

                        {/* Lista */}
                        <Stack spacing={1}>
                            {eventos.map((a) => {
                                const sev = severidad(a.tipo);
                                const pal = theme.palette[sev];
                                return (
                                    <Paper key={a.id} variant="outlined" sx={{ p: 1.5, borderColor: alpha(pal.main, 0.3) }}>
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <AlarmIcon sx={{ color: `${sev}.main`, fontSize: 20 }} />
                                            <Box sx={{ minWidth: 0 }}>
                                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                    <Chip size="small" label={a.tipo}
                                                        sx={{
                                                            height: 20, fontSize: 9, fontWeight: 700,
                                                            bgcolor: alpha(pal.main, 0.12), color: `${sev}.main`,
                                                            '& .MuiChip-label': { px: 1 },
                                                        }}
                                                    />
                                                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>
                                                        {a.tanque || 'Planta'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                                                        {dayjs(a.ts).format('DD/MM/YYYY HH:mm:ss')}
                                                    </Typography>
                                                </Stack>
                                                {a.detalle && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                                                        {a.detalle}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}